<script lang="ts" setup>
import BaseButton from '@/core/components/base/BaseButton.vue';
import Customer from "@/customer/interfaces/Customer";
import PrescriptionRenewalNotifySMS from "@/prescription/components/PrescriptionRenewalNotifySMS.vue";
import PrescriptionRenewalNotifyPush from "@/prescription/components/PrescriptionRenewalNotifyPush.vue";
import {NotificationTemplate} from "@/notification-template/interfaces";
import {computed} from "vue";
import {renewalRemindersAreValid} from "@/prescription/helpers/renewalPlanHelper";
import PrescriptionRenewalStepTitle from "@/prescription/components/PrescriptionRenewalStepTitle.vue";
import {Checkbox} from "@/core/components/ui/checkbox";
import {Label} from "@/core/components/ui/label";
import NotificationPreview from "@/core/components/notification-preview/NotificationPreview.vue";
import {useSystemStore} from "@/stores/system.store";
import PrescriptionRenewalNotifyCustomerStatus
    from "@/prescription/components/PrescriptionRenewalNotifyCustomerStatus.vue";

defineProps<{
    customer: Customer;
    dates: Date[];
    notifications: NotificationTemplate[];
}>();
const {environment} = useSystemStore();
defineEmits(['next-step', 'previous-step']);

const customerPhone = defineModel<string | null>("customerPhone", {required: true});
const notifyCreated = defineModel<boolean>("notifyCreated", {required: true});
const notifyRenewals = defineModel<boolean>("notifyRenewals", {required: true});
const selectedNotificationChannel = defineModel<"sms" | "push">("selectedNotificationChannel", {required: true});
const isDisabled = computed(() => !renewalRemindersAreValid({
    notifyRenewals: notifyRenewals.value,
    notifyCreated: notifyCreated.value,
    channel: selectedNotificationChannel.value,
    phone: customerPhone.value
}));

const renewalCreatedPushContent = computed(() => {
    return `Message de la ${environment?.entity.name || 'pharmacie'}: n'oubliez pas de renouveler votre ordonnance. Retrouvez vos ordonnances sur https://www.apodis-sante.com/apodis/`;
});
</script>

<template>
  <div class="flex flex-col gap-y-4">
    <div class="space-y-2">
      <div class="space-y-2">
        <PrescriptionRenewalStepTitle title="Rappels automatiques" back @back="$emit('previous-step')" />
        <p>Souhaitez-vous envoyer un rappel automatique au patient ?</p>
      </div>
      <div class="space-y-2">
        <div class="flex items-center gap-2">
          <Checkbox id="renewals" v-model:model-value="notifyRenewals" class="w-6 h-6" />
          <Label for="renewals" class="text-base font-bold">Rappel 3 jours avant chaque renouvellement</Label>
        </div>
        <div v-if="notifyRenewals" class="mb-8">
          <div v-if="selectedNotificationChannel === 'sms'">
            <PrescriptionRenewalNotifySMS
              :notifications="notifications"
              :customer="customer"
              :dates="dates"
              template-slug="upcoming"
              when="3 jours avant chaque renouvellement"
            />
          </div>
          <div v-else>
            <PrescriptionRenewalNotifyPush
              :notifications="notifications"
              :customer="customer"
              :dates="dates"
            />
          </div>
        </div>
      </div>
      <div class="space-y-2">
        <div class="flex items-center gap-2">
          <Checkbox id="notifyCreated" v-model:model-value="notifyCreated" class="w-6 h-6" />
          <Label for="notifyCreated" class="text-base font-bold">Envoyer une notification juste après la création du
            plan
            de renouvellement</Label>
        </div>
        <div v-if="notifyCreated">
          <div v-if="selectedNotificationChannel === 'push'">
            <h3 class="mb-3">
              Le patient recevra :
            </h3>
            <NotificationPreview
              :content=" renewalCreatedPushContent"
              type="push"
              :count="1"
              when="À la création du plan"
            />
          </div>
          <PrescriptionRenewalNotifySMS
            v-else
            :notifications="notifications"
            :customer="customer"
            :dates="dates"
            template-slug="created"
            when="À la création du plan"
          />
        </div>
      </div>
      <div v-if="notifyCreated || notifyRenewals">
        <div class="h-1 w-full bg-primary my-4" />
        <PrescriptionRenewalNotifyCustomerStatus
          v-model:customer-phone="customerPhone"
          :customer="customer"
          :channel="selectedNotificationChannel"
        />
      </div>
    </div>
    <div class="flex flex-row justify-end py-4">
      <base-button primary :disabled="isDisabled" @click="$emit('next-step')">
        Continuer
      </base-button>
    </div>
  </div>
</template>
